import React, { useState } from 'react';

export interface PieSlice {
  key: string;
  label: string;
  value: number;
  color: string;
}

interface PieChartProps {
  data: PieSlice[];
  activeKeys: Set<string>;      // currently-selected slice keys (for highlighting)
  onSliceClick: (key: string) => void;
  size?: number;
  title?: string;
}

const polar = (cx: number, cy: number, r: number, angleDeg: number): [number, number] => {
  const a = ((angleDeg - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
};

const PieChart: React.FC<PieChartProps> = ({ data, activeKeys, onSliceClick, size = 200, title }) => {
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const total = data.reduce((sum, d) => sum + d.value, 0);
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 8;     // leave room for the hovered slice to pop out
  const hasActive = activeKeys.size > 0;

  if (total === 0) {
    return (
      <div style={{ textAlign: 'center', color: '#a09070', margin: '1rem 0' }}>
        {title && <div style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600, color: '#5a4a2a', marginBottom: '0.5rem' }}>{title}</div>}
        No data to chart yet.
      </div>
    );
  }

  let angle = 0;
  const slices = data
    .filter(d => d.value > 0)
    .map(d => {
      const startAngle = angle;
      const sweep = (d.value / total) * 360;
      const endAngle = startAngle + sweep;
      const midAngle = startAngle + sweep / 2;
      angle = endAngle;

      const isHovered = hoveredKey === d.key;
      const dim = (hasActive && !activeKeys.has(d.key)) && !isHovered;
      const isFull = sweep >= 359.999;

      let path: string;
      if (isFull) {
        // A single 100% slice — draw two half-arcs to make a full circle.
        const [mx, my] = polar(cx, cy, r, 180);
        path = `M ${cx} ${cy - r} A ${r} ${r} 0 1 1 ${mx} ${my} A ${r} ${r} 0 1 1 ${cx} ${cy - r} Z`;
      } else {
        const [x1, y1] = polar(cx, cy, r, startAngle);
        const [x2, y2] = polar(cx, cy, r, endAngle);
        const largeArc = sweep > 180 ? 1 : 0;
        path = `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;
      }

      // Pop the hovered slice out a few px along its mid-angle.
      const pop = isHovered && !isFull ? 8 : 0;
      const [ux, uy] = polar(0, 0, 1, midAngle);
      const transform = `translate(${ux * pop} ${uy * pop})`;

      return (
        <path
          key={d.key}
          d={path}
          fill={d.color}
          stroke="#fff"
          strokeWidth={isHovered ? 3 : 2}
          opacity={dim ? 0.3 : 1}
          transform={transform}
          style={{ cursor: 'pointer', transition: 'opacity 0.15s, transform 0.15s' }}
          onMouseEnter={() => setHoveredKey(d.key)}
          onMouseLeave={() => setHoveredKey(prev => (prev === d.key ? null : prev))}
          onClick={() => onSliceClick(d.key)}
        />
      );
    });

  const hovered = data.find(d => d.key === hoveredKey && d.value > 0);
  const caption = hovered
    ? `${hovered.label}: ${hovered.value} (${Math.round((hovered.value / total) * 100)}%)`
    : `Total: ${total}`;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', margin: '1rem 0' }}>
      {title && (
        <div style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600, fontSize: '1rem', color: '#5a4a2a' }}>
          {title}
        </div>
      )}
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {slices}
      </svg>
      <div
        style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: '0.95rem',
          fontWeight: 600,
          color: hovered ? hovered.color : '#5a4a2a',
          minHeight: '1.2em',
          transition: 'color 0.15s',
        }}
      >
        {caption}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '0.5rem 1rem' }}>
        {data.filter(d => d.value > 0).map(d => {
          const isHovered = hoveredKey === d.key;
          const dim = (hasActive && !activeKeys.has(d.key)) && !isHovered;
          return (
            <button
              key={d.key}
              onClick={() => onSliceClick(d.key)}
              onMouseEnter={() => setHoveredKey(d.key)}
              onMouseLeave={() => setHoveredKey(prev => (prev === d.key ? null : prev))}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.4rem',
                background: 'none', border: 'none', cursor: 'pointer',
                fontFamily: "'Playfair Display', serif", fontSize: '0.85rem',
                color: '#5a4a2a', opacity: dim ? 0.4 : 1, padding: 0,
                textDecoration: isHovered ? 'underline' : 'none',
              }}
            >
              <span style={{ width: 12, height: 12, borderRadius: 3, background: d.color, display: 'inline-block' }} />
              {d.label} ({d.value})
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default PieChart;
